import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { StewartDialog, StewartDialogActions } from "controls/global/dialogs/stewart-dialog/StewartDialog";
import { Box, IconButton, Typography, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { borderRadius, colors, fontSize, fontWeight, gradients, iconSize, margin, padding } from "theme/defaultStyle";
import ActionButton from "controls/global/action-button";
import { ActionResult, FileUploadAdditionalInfo, FileUploadStatus, Message } from "entities/UIModel";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { TitleMessageUI } from "entities/UIModel/TitleMessageUI";
import TextInputField from "controls/global/text-input-field/TextInputField";
import SendMessageIcon from "theme/icons/SendMessageIcon";
import { FileWithPath } from "file-selector";
import { FileRejection } from "react-dropzone";
import { ActionResultStatus, DocumentType, MessageType, ProgressImageType, UIConstants } from "utils/data/enum";
import UploadDropZone from "controls/global/upload-files/UploadDropZone";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import { getFormattedErrorMessage, mapFileUploaResponse } from "pages/file/utils/fileUploadHelper";
import { useDocument } from "utils/context/DocumentContext";
import ConfirmationDialogV3 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV3";
import { useMessage } from "utils/context/TitleMessageContext";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { useWatch } from "react-hook-form";
import { showToast } from "controls/global/stewart-toast/showToast";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import UploadStatusGridEdit from "./UploadStatusGridEdit";
import { UWADocument as UiUWADocument } from "entities/UIModel/UWADocument";
import UploadStatusGrid from "./UploadStatusGrid";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "calc(50vw)",
    minWidth: "calc(45vw)",
    display: "flex",
    justifyContent: "flex-start",
    padding: "36px 30px 0px 40px",
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "24px",
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  paddingBottom: padding.xlarge22,
  paddingRight: padding.zero,
  paddingLeft: padding.zero,
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: padding.xsmall1,
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const UploadHeading = styled(Typography)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.bold1,
  textTransform: "capitalize",
});

const ContentWrapper = styled(Box)({
  borderRadius: borderRadius.medium,
  padding: padding.medium1,
  marginTop: margin.xlarge1,
  maxWidth: "100%",
  position: "relative",
});

interface MessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  message?: (TitleMessageUI & { isToggled?: boolean }) | null;
  isToggled?: boolean;
  onToggle?: (rowId: string) => void;
  notDisabled?: boolean;
  productIndex?: number;
  parentIntegrationKey?: string;
  performSearchDocument?: () => Promise<void>;
  performSearch?: () => Promise<void>;
  saveFile: () => Promise<void>;
  reloadFile: () => Promise<void>;
  reloadFileActivities: () => Promise<void>;
  key?: string;
}

const DEFAULT_DELETE_PROMPT = "Delete";

const SendMessageDocumentDialog = ({
  isOpen,
  onClose,
  notDisabled,
  productIndex,
  parentIntegrationKey,
  performSearchDocument,
  performSearch,
  saveFile,
  reloadFileActivities,
  reloadFile,
}: MessageDialogProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const { getValues, setValue } = useFormWrapper();
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] = useAutomaticProgressDialogActions();
  const [{ profileSettings }] = useProfileSettingsCache();

  const SUBMIT_SUCCESS = "Message/documents submitted successfully!";
  const SUBMIT_FAILED = "Message/documents submission failed!";

  // SUCCESS grid state (only successful uploads live here)
  const [fileUploadStatuses, setFileUploadStatuses] = useState<FileUploadStatus[]>([]);

  // Failure handling refs/state
  const successUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const failedUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const [hasFailedUpload, setHasFailedUpload] = useState<boolean>(false);

  // Upload/progress
  const isProgressCompletedRef = useRef<boolean>(false);
  const uploadSessionRef = useRef<string>("");
  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false);
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [runProgress, setRunProgress] = useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = useState<string>("");
  const [isDocEditMode, setIsDocEditMode] = React.useState(false);

  // Deletion confirm dialog
  const [isConfirmatinDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const tobeDeletedDocumentId = useRef<number | undefined>(undefined);

  // Rename cache per ID
  const [editedNameById, setEditedNameById] = useState<Record<number, string>>({});

  // External APIs
  const [, { uploadFiles, deleteUwaDocument }] = useDocument();
  const [, { setBypassDefaultLoader, setPercentageComplete }] = useProcessStatusTracking();
  const [, { sendUnderWriterMessageDoc, deleteUWADocuments }] = useMessage();

  // Form-driven values
  const orderId = getValues(`uwaProducts.${productIndex}.orderID`) || 0;
  const fileId = getValues("id") || 0;
  const uwaRequestID: number = getValues(`uwaProducts.${productIndex}.uwaRequest.uwaRequestID`) || 0;
  const orderIdFromForm: number = getValues(`uwaProducts.${productIndex}.orderID`) || 0;

  const messageBodyText = useWatch({
    name: `uwaProducts.${productIndex}.messageText`,
  });

  const [sending, setSending] = useState(false);

  const isSendDisabled = useMemo(() => {
    const trimmed = messageBodyText?.trim() || "";
    return (trimmed === "" && fileUploadStatuses.length === 0) || trimmed.length > 2500;
  }, [messageBodyText, fileUploadStatuses]);

  const handleOnClose = () => {
    onClose && onClose();
  };

  const handleOnCloseProgressDialog = async () => {
    isProgressCompletedRef.current = true;
    if (uploadCompleted) {
      setOpenProgressDialog(false);
      setRunProgress(false);
      setProgressRequestId("");
    }
  };

  const handleClickOnDone = async () => {
    onClose && onClose();

    // delete any uploaded docs that were added in this session if desired
    const uploadedDocs = successUploadFilesRef.current;
    const documentIdsToDelete = uploadedDocs
      .filter((doc) => doc.isUploadSuccess && doc.documentId)
      .map((doc) => doc.documentId!);

    if (documentIdsToDelete.length > 0) {
      try {
        await deleteUWADocuments(documentIdsToDelete);
      } catch (err) {
        console.error("Failed to delete uploaded documents:", err);
      }
    }

    // reset
    successUploadFilesRef.current = [];
    failedUploadFilesRef.current = [];
    setFileUploadStatuses([]);
    setOpenProgressDialog(false);
    setHasFailedUpload(false);
    setEditedNameById({});
  };

  function getFilenameExtension(filename: string) {
    const filenameParts = filename.split('.');
    return filenameParts.length > 1 ? filenameParts.pop() : '';
  } 

  const deleteUploadedImage = async () => {
    if (tobeDeletedDocumentId.current) {
      await deleteUwaDocument(tobeDeletedDocumentId.current);

      successUploadFilesRef.current = successUploadFilesRef.current.filter(
        (f) => f.documentId !== tobeDeletedDocumentId.current,
      );
      setFileUploadStatuses((prev) => prev.filter((f) => f.documentId !== tobeDeletedDocumentId.current));
    }
    setConfirmationDialogOpen(false);
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
    tobeDeletedDocumentId.current = undefined;
  };

  const handleDialogCancel = () => {
    setConfirmationDialogOpen(false);
    tobeDeletedDocumentId.current = undefined;
  };

  const handleOnUpload = async (acceptedFiles: FileWithPath[], rejectedFiles: FileRejection[]) => {
    setUploadCompleted(false);

    const prId = uuidv4();
    setProgressRequestId(prId);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);
    isProgressCompletedRef.current = false;

    const curFileId: number = getValues("id") || 0;
    const curOrderId: number = getValues(`uwaProducts.${productIndex}.orderID`) || 0;
    const curUwaRequestID: number = getValues(`uwaProducts.${productIndex}.uwaRequest.uwaRequestID`) || 0;

    const fileAdditionalInfos: FileUploadAdditionalInfo[] = [];
    const formData = new FormData();

    const agency = getValues("agency");
    const companyID = agency?.id || "";
    const clientFileID = getValues("fileNameNumber") || "";
    const companyLegacyID = agency?.legacyID || "";

    formData.set("companyID", companyID);
    formData.set("fileID", String(curFileId));
    formData.set("orderID", String(curOrderId));
    formData.set("uploadSessionKey", uploadSessionRef.current);
    formData.set("clientFileID", clientFileID);
    formData.set("companyLegacyID", companyLegacyID);
    formData.set("uwaRequestID", String(curUwaRequestID));
    formData.set("documentStatusType", "DRAFT");

    let index = 0;
    acceptedFiles?.forEach((file) => {
      formData.append("files", file, file.name);
      fileAdditionalInfos.push({
        index,
        filePath: file.path || "",
        fileName: file.name,
        fileSize: file.size,
        errorMessage: "",
        isValidFile: true,
      });
      index++;
    });

    rejectedFiles?.forEach((rejectedFile) => {
      fileAdditionalInfos.push({
        index,
        filePath: "",
        fileName: rejectedFile.file.name,
        fileSize: rejectedFile.file.size,
        errorMessage: getFormattedErrorMessage(rejectedFile.errors),
        isValidFile: false,
      });
      index++;
    });

    formData.append("fileUploadAdditionalInfo", JSON.stringify(fileAdditionalInfos));

    const actionResult: ActionResult = await uploadFiles(prId, formData, DocumentType.FILE);

    if (actionResult.status === ActionResultStatus.Success) {
      const uploadedResult = actionResult.apiResponse;
      const mapped: FileUploadStatus[] = mapFileUploaResponse(uploadedResult) ?? [];

      const successes = mapped.filter((f) => f.isUploadSuccess);
      const failures = mapped.filter((f) => !f.isUploadSuccess);

      successUploadFilesRef.current = [...successUploadFilesRef.current, ...successes];
      if (failures.length > 0) {
        failedUploadFilesRef.current = [...failedUploadFilesRef.current, ...failures];
        setHasFailedUpload(true);
      }
      setFileUploadStatuses((prev) => [...prev, ...successes]);
    } else {
      showToast({
        type: "error",
        title: UIConstants.UNEXPECTED_ERROR_MESSAGE.toString(),
      });
    }

    setPercentageComplete(prId);
    setUploadCompleted(true);
  };

const handleOnDelete = async (documentId: number) => {
  tobeDeletedDocumentId.current = documentId;

  const tobeDeletedFile =
    successUploadFilesRef.current.find((f) => f.documentId === documentId) ??
    fileUploadStatuses.find((f) => f.documentId === documentId);

  if (!tobeDeletedFile) {
    setConfirmationMessage(`Delete this document?`);
  } else {
    const id = tobeDeletedFile.documentId!;
    const displayName = editedNameById[id] ?? tobeDeletedFile.documentDisplayFileName;
    const ext = getFilenameExtension(tobeDeletedFile.documentOriginalFileName ?? "");
    const finalName = ext ? `${displayName}.${ext}` : displayName;

    setConfirmationMessage(`${DEFAULT_DELETE_PROMPT} '${finalName}'?`);
  }

  setConfirmationDialogOpen(true);
};


  function removeFileNameExtension(filename: string) {
    const index = filename.lastIndexOf(".");
    return index === -1 ? filename : filename.substring(0, index);
  }

  const handleSend = async () => {
    openAutomaticProgressDialog(UIConstants.TITLE_SEARCH_SEND_MESSAGE_AND_DOCUMENT_IN_PROGRESS_MESSAGE);
    setSending(true);

    const messageBody = getValues(`uwaProducts.${productIndex}.messageText`);
    const messageSubject = getValues(`uwaProducts.${productIndex}.messageSubject`) || "";
    const needByDate = getValues(`uwaProducts.${productIndex}.needByDate`) || null;

    const myDocuments: UiUWADocument[] = successUploadFilesRef?.current?.map((doc) => {
      const id = doc.documentId!;
      const effectiveName = editedNameById[id] ?? doc.documentDisplayFileName;

      return {
        fileID: fileId,
        orderID: orderId,
        uwaDocumentID: id,
        displayFileName: removeFileNameExtension(effectiveName ?? ""),
        filepath: doc.documentPath,
        fileExt: getFilenameExtension(doc.documentOriginalFileName ?? ''),
        uwaRequestID: uwaRequestID,
        uwaDocumentTypeCode: DocumentType.FILE,
        errorMessage: doc.errorMessage,
        isUploadSuccess: doc.isUploadSuccess,
        uniqueIdentifier: "",
        integrationKey: uuidv4(),
        documentDisplayFileName :removeFileNameExtension(effectiveName ?? ""),
      };
    });

    const documentOnly = (!messageBody || messageBody.trim() === "") && myDocuments.length > 0;

    const scMessage: Message = {
      integrationKey: uuidv4(),
      messageID: 0,
      orderID: orderId,
      messageSubject,
      messageText: messageBody || null,
      messageCreatedByUserID: profileSettings.userID,
      needByDate: needByDate ?? null,
      isSent: 0,
      isHidden: 0,
      forStaffViewOnly: 0,
      fileID: fileId,
      uwaRequestID: uwaRequestID,
      uwadocuments: myDocuments,
      messageTypeCode: MessageType.Message,
      isNew: true,
    };

    try {
      if (!sending) {
        await sendUnderWriterMessageDoc(scMessage, documentOnly);
        if (performSearch) await performSearch();
        if (performSearchDocument) await performSearchDocument();

        closeAutomaticProgressDialog();
        showToast({ type: "success", title: SUBMIT_SUCCESS });
        handleOnClose();
      }
    } catch (err) {
      console.error("Send failed", err);
      closeAutomaticProgressDialog();
      showToast({ type: "error", title: SUBMIT_FAILED });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    setOpen(isOpen);
    successUploadFilesRef.current = [];
    failedUploadFilesRef.current = [];
    isProgressCompletedRef.current = false;

    if (isOpen && productIndex !== undefined) {
      setValue(`uwaProducts.${productIndex}.messageText`, "");
    }
  }, [isOpen, productIndex, setValue]);

  useEffect(() => {
    uploadSessionRef.current = uuidv4();
  }, []);

  useEffect(() => {
    if (uploadCompleted && openProgressDialog) {
      handleOnCloseProgressDialog();
    }
  }, [uploadCompleted, openProgressDialog]);

  const uiDocuments: UiUWADocument[] = useMemo(() => {
    const getExt = (name?: string) => {
      if (!name) return "";
      const i = name.lastIndexOf(".");
      return i > 0 && i < name.length - 1 ? name.slice(i + 1) : "";
    };

    // Build UI documents only from successful uploads (state already filtered to successes)
    return (fileUploadStatuses ?? [])
      .filter((s) => s.isUploadSuccess && typeof s.documentId === "number")
      .map((s) => {
        const id = s.documentId as number;
        const effectiveName = editedNameById[id] ?? s.documentOriginalFileName;
        const ext = getExt(effectiveName);

        const doc: UiUWADocument = {
          uwaDocumentID: id,
          uwaRequestID,
          fileID: fileId,
          orderID: orderIdFromForm,
          integrationKey: (s as any).integrationKey,
          origCreatedBy: undefined as any,
          documentCreatedDate: undefined as any,
          filePath: s.documentPath,
          fileName: effectiveName,
          displayFileName: effectiveName,
          documentOriginalFileName: s.documentOriginalFileName,
          documentDisplayFileName :s.documentDisplayFileName,
          documentStatusTypeCode: "DRAFT",
          uniqueIdentifier: uuidv4(),
          documentID: id,
          fileExt: ext,
          rowId: id,
          isEdit: false as any,
        };
        return doc;
      });
  }, [fileUploadStatuses, uwaRequestID, orderIdFromForm, fileId, editedNameById]);

  return (
    <>
      <StyledDialog open={open}>
        <StyledDialogTitle
          id="styledDialogTitleDebug"
          sx={{ paddingLeft: "10px" }}
        >
          <IconContainer sx={{ color: "white" }}>
            <SendMessageIcon />
          </IconContainer>
          <Typography id="uploadPolicyImageTitleDebug">Send Message/ Upload Documents</Typography>
          <CloseIconContainer>
            <IconButton
              className="iconButton"
              onClick={handleClickOnDone}
            >
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>

        <ContentWrapper>
          <TextInputField
            name={`uwaProducts.${productIndex}.messageText`}
            multiline
            errorMaxLength={2500}
            errorMessageExceedLimit={"Cannot exceed 2,500 characters"}
            placeholder="Messages"
            label="Messages"
          />

          <Box>
            <UploadDropZone
              handleOnUpload={handleOnUpload}
              documentType={DocumentType.FILE}
            />

            {(failedUploadFilesRef.current?.length > 0 || hasFailedUpload) && (
              <Box mt={4}>
                <UploadHeading>Failed to Upload ({failedUploadFilesRef.current?.length ?? 0})</UploadHeading>
                <UploadStatusGrid
                  uploadRequestId="failed-1"
                  fileUploadStatuses={failedUploadFilesRef.current}
                />
              </Box>
            )}

            {fileUploadStatuses.length > 0 && (
              <Box mt={4}>
                <UploadHeading gutterBottom>Uploaded Documents ({fileUploadStatuses.length})</UploadHeading>
                <UploadStatusGridEdit
                  fileUploadStatuses={fileUploadStatuses /* already successes only */}
                  handleOnDelete={handleOnDelete}
                  documents={uiDocuments}
                  handleOnRename={(documentId, newName) => {
                    setEditedNameById((prev) => ({ ...prev, [documentId]: newName }));
                  }}
                  onSaveDocument={saveFile}
                  onEditModeChange={setIsDocEditMode}
                />
              </Box>
            )}
          </Box>

          <StyledDialogAction>
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={handleClickOnDone}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="outlined"
              color="primary"
              onClick={handleSend}
              disabled={isSendDisabled || isDocEditMode }
            >
              Send
            </ActionButton>
          </StyledDialogAction>
        </ContentWrapper>

        <ConfirmationDialogWithProgressbar
          autoClose={true}
          title="Please wait while uploading your documents ..."
          closeButtonText="Done! Click to Continue"
          isOpen={openProgressDialog}
          onClose={handleOnCloseProgressDialog}
          requestId={progressRequestId}
          runProgress={runProgress}
          progressImageType={ProgressImageType.circular}
          delay={1000}
          maxDelayCounter={90}
          maxZeroPctCompleteCount={30}
        />

        <ConfirmationDialogV3
          isOpen={isConfirmatinDialogOpen}
          confirmationMessage={confirmationMessage}
          cancelActionButton={true}
          onYes={deleteUploadedImage}
          onNo={closeConfirmationDialog}
          onCancel={handleDialogCancel}
          singleActionButtonText={"Delete"}
          buttonNoText={"Cancel"}
        />
      </StyledDialog>
    </>
  );
};

export default SendMessageDocumentDialog;
